import { ExternalLink } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Button from "../../lib/ui/components/button/Button";
import "../../lib/ui/components/link/link.css";
import Dropdown from "../../lib/ui/components/dropdown/Dropdown";
import MatchRating from "../../lib/ui/components/match-rating/MatchRating";
import {
    BodyText,
    Heading4,
    Heading6,
    XsmallText,
} from "../../lib/ui/components/typography/Typography";
import PageLoader from "../JobDisplay/PageLoader";
import RoleNotSetUpEmptyState from "./RoleNotSetUpEmptyState";
import JobSettingsSectionNav from "./JobSettingsSectionNav";
import RejectApplicantModal from "../CandidateManagement/RejectApplicantModal";
import { isSafeExternalUrl, toAnalysisApplicant } from "../CandidateManagement/atsApplications";
import {
    copyToClipboardHandler,
    exportToPDFHandler,
} from "../CandidateManagement/ExportAnalysisHelper";
import { isRejectSupported } from "../../lib/constants";
import {
    DIGEST_OVERVIEW,
    REJECT_CANDIDATES,
    SEND_CALENDAR_LINK,
} from "../../lib/urls";

const OVERVIEW_SECTION = {
    highConfidence: "high-confidence",
    toReview: "to-review",
    worthALook: "worth-a-look",
    flagged: "flagged",
};

function CandidateRow({
    candidate,
    onOpenApplicant,
    onSendCalendarLink,
    sendingCalendar = false,
    showFlagReason = false,
    getMenuItems,
}) {
    if (!candidate) return null;

    const menuItems = getMenuItems ? getMenuItems(candidate) : null;

    return (
        <div
            className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
            data-side-panel-keep-open
        >
            <div className="flex min-w-0 max-w-xl flex-col gap-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="text-label font-bold">{candidate.name}</span>
                    <MatchRating rating={candidate.rating} />
                    {candidate.ats_stage && (
                        <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-body text-gray-600">
                            In {candidate.ats_stage}
                        </span>
                    )}
                </div>
                {showFlagReason && candidate.flag_reason && (
                    <BodyText weight="semibold" emphasis>
                        {candidate.flag_reason}
                    </BodyText>
                )}
                {candidate.blurb && (
                    <BodyText className="line-clamp-2">{candidate.blurb}</BodyText>
                )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-1">
                {/* Always visible below lg, where hover is unreliable. At lg
                    and up, hidden until the card is hovered (or a button
                    inside is focused), then slides in from the right. */}
                <div className="pointer-events-auto flex translate-x-0 flex-wrap items-center justify-end gap-2 opacity-100 transition-all duration-200 ease-out lg:pointer-events-none lg:translate-x-2 lg:opacity-0 lg:focus-within:pointer-events-auto lg:focus-within:translate-x-0 lg:focus-within:opacity-100 lg:group-hover:pointer-events-auto lg:group-hover:translate-x-0 lg:group-hover:opacity-100">
                    {isSafeExternalUrl(candidate.ats_profile_url) && (
                        <a
                            href={candidate.ats_profile_url.trim()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="custom-link inline-flex items-center gap-1.5 text-small"
                            title="Opens in your ATS (requires ATS login)"
                        >
                            View ATS profile
                            <ExternalLink className="h-3 w-3" aria-hidden />
                        </a>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => onOpenApplicant(candidate.session_id)}
                    >
                        View details
                    </Button>
                    {candidate.candidate_id && (
                        <Button
                            variant="primary"
                            disabled={sendingCalendar}
                            onClick={() => onSendCalendarLink(candidate)}
                        >
                            {sendingCalendar ? "Sending..." : "Send Calendar Link"}
                        </Button>
                    )}
                </div>
                {menuItems && (
                    <Dropdown
                        id={`overview-actions-${candidate.session_id || candidate.name}`}
                        items={menuItems}
                        placement="bottom-end"
                    />
                )}
            </div>
        </div>
    );
}

function CandidateList({
    candidates,
    emptyCopy,
    onOpenApplicant,
    onSendCalendarLink,
    sendingCandidateId,
    tint,
    showFlagReason,
    getMenuItems,
    /** Lay candidates out as a responsive card grid instead of stacked rows. */
    asCards = false,
}) {
    const containerClass = tint || "bg-white shadow hover:bg-gray-50";

    if (!candidates?.length) {
        if (!emptyCopy) return null;
        return <BodyText>{emptyCopy}</BodyText>;
    }

    if (asCards) {
        return (
            <div className="flex flex-col gap-3">
                {candidates.map((candidate, index) => (
                    <div
                        key={candidate.session_id || `${candidate.name}-${index}`}
                        className={`group w-full min-w-0 rounded-lg px-4 py-3.5 transition-colors ${containerClass}`}
                    >
                        <CandidateRow
                            candidate={candidate}
                            onOpenApplicant={onOpenApplicant}
                            onSendCalendarLink={onSendCalendarLink}
                            sendingCalendar={sendingCandidateId === candidate.candidate_id}
                            showFlagReason={showFlagReason}
                            getMenuItems={getMenuItems}
                        />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={`overflow-hidden rounded-lg ${containerClass}`}>
            {candidates.map((candidate, index) => (
                <div
                    key={candidate.session_id || `${candidate.name}-${index}`}
                    className={`group px-4 py-3.5 transition-colors hover:bg-gray-50 ${
                        index < candidates.length - 1 ? "border-b border-gray-200" : ""
                    }`}
                >
                    <CandidateRow
                        candidate={candidate}
                        onOpenApplicant={onOpenApplicant}
                        onSendCalendarLink={onSendCalendarLink}
                        sendingCalendar={sendingCandidateId === candidate.candidate_id}
                        showFlagReason={showFlagReason}
                        getMenuItems={getMenuItems}
                    />
                </div>
            ))}
        </div>
    );
}

/**
 * Role Overview: the shortlist digest sections for this role
 * (stats, high confidence, and the other candidate lists).
 */
function formatAsOfTimestamp(date) {
    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }).format(date);
}

export default function RoleDigestOverview({
    companyId,
    jobId,
    token,
    onOpenApplicant,
    needsSetup = false,
    onSetUpRole,
    /** Full applicant objects from JobDetails, for evaluation export + reject. */
    applicants = [],
    roleName = "",
    companyName = "",
    company = null,
    onCandidatesUpdate,
}) {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [sendingCandidateId, setSendingCandidateId] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [isRejecting, setIsRejecting] = useState(false);

    const applicantsBySessionId = useMemo(() => {
        const map = new Map();
        (applicants || []).forEach((applicant) => {
            const sessionId =
                applicant?.interview_session?.id || applicant?.interview_session_id;
            if (sessionId) map.set(String(sessionId), applicant);
        });
        return map;
    }, [applicants]);

    const rejectSupported = isRejectSupported(company?.ats_integration_id);

    const getMenuItems = (candidate) => {
        const rawApplicant = candidate.session_id
            ? applicantsBySessionId.get(String(candidate.session_id))
            : null;
        const analysisApplicant = rawApplicant
            ? toAnalysisApplicant(rawApplicant)
            : null;
        const hasEvaluation = Boolean(analysisApplicant?.ai_insights);
        const evaluationTitle = hasEvaluation
            ? undefined
            : "Evaluation isn't available for this applicant yet.";

        return [
            {
                label: "Download Evaluation",
                disabled: !hasEvaluation,
                title: evaluationTitle,
                onClick: async () => {
                    await exportToPDFHandler(analysisApplicant, roleName, companyName);
                },
            },
            {
                label: "Copy Evaluation",
                disabled: !hasEvaluation,
                title: evaluationTitle,
                onClick: () =>
                    copyToClipboardHandler(analysisApplicant, roleName, companyName),
            },
            {
                label: "Reject",
                variant: "danger",
                disabled:
                    !rejectSupported ||
                    !candidate.candidate_id ||
                    rawApplicant?.status === "ATS_DECLINED",
                title: rejectSupported
                    ? undefined
                    : "Your ATS doesn't allow this feature.",
                onClick: () => setRejectTarget(candidate),
            },
        ];
    };

    const handleRejectConfirm = async ({ rejection_reason_id, note }) => {
        if (!rejectTarget?.candidate_id || !companyId || !jobId || !token) return;
        setIsRejecting(true);
        try {
            const response = await fetch(REJECT_CANDIDATES(companyId, jobId), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    candidate_ids: [rejectTarget.candidate_id],
                    rejection_reason_id,
                    note,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Failed to reject applicant");
            }
            toast.success(`${rejectTarget.name} has been rejected.`);
            setRejectTarget(null);
            if (onCandidatesUpdate) await onCandidatesUpdate();
            loadOverview({ silent: true });
        } catch (error) {
            toast.error(error.message || "Failed to reject applicant");
        } finally {
            setIsRejecting(false);
        }
    };

    const sendCalendarLink = async (candidate) => {
        if (!candidate?.candidate_id || !companyId || !jobId || !token) {
            toast.error("Unable to send calendar link. Try refreshing the page.");
            return;
        }
        setSendingCandidateId(candidate.candidate_id);
        try {
            const response = await fetch(SEND_CALENDAR_LINK(companyId, jobId), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ candidate_ids: [candidate.candidate_id] }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Failed to send calendar link");
            }
            toast.success(`Calendar link sent to ${candidate.name}`);
        } catch (error) {
            toast.error(error.message || "Failed to send calendar link");
        } finally {
            setSendingCandidateId(null);
        }
    };

    const loadOverview = useCallback(
        ({ silent = false } = {}) => {
            if (needsSetup || !jobId || !token) return undefined;
            let cancelled = false;
            if (!silent) setLoading(true);
            fetch(DIGEST_OVERVIEW(jobId), {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then(async (response) => {
                    const data = await response.json().catch(() => ({}));
                    if (!response.ok) {
                        throw new Error(data.detail || "Could not load the overview");
                    }
                    return data;
                })
                .then((data) => {
                    if (!cancelled) {
                        setOverview(data);
                        setRefreshedAt(new Date());
                    }
                })
                .catch((error) => {
                    if (!cancelled) {
                        if (!silent) {
                            setOverview(null);
                            setRefreshedAt(null);
                        }
                        toast.error(error.message || "Could not load the overview");
                    }
                })
                .finally(() => {
                    if (!cancelled && !silent) setLoading(false);
                });
            return () => {
                cancelled = true;
            };
        },
        [jobId, token, needsSetup]
    );

    useEffect(() => loadOverview(), [loadOverview]);

    if (needsSetup) {
        return <RoleNotSetUpEmptyState onSetUp={onSetUpRole} />;
    }

    if (loading) {
        return (
            <div className="flex w-full justify-center py-16">
                <PageLoader />
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="px-6 py-10">
                <BodyText>The overview for this role could not be loaded.</BodyText>
            </div>
        );
    }

    const topRated = overview.top_rated || [];
    const toReview = overview.to_review || [];
    // worth_a_look is a list since the overview went uncapped; tolerate the
    // older single-candidate shape from a stale backend.
    const worthALook = Array.isArray(overview.worth_a_look)
        ? overview.worth_a_look
        : overview.worth_a_look
          ? [overview.worth_a_look]
          : [];
    const flagged = overview.flagged_for_review || [];
    const toReviewTotal =
        toReview.length + (overview.to_review_extra > 0 ? overview.to_review_extra : 0);

    const sections = [
        {
            id: OVERVIEW_SECTION.highConfidence,
            label: `High confidence (${topRated.length})`,
            count: topRated.length,
        },
        {
            id: OVERVIEW_SECTION.toReview,
            label: `Top applicants (${toReviewTotal})`,
            count: toReviewTotal,
        },
        {
            id: OVERVIEW_SECTION.worthALook,
            label: `Worth a look (${worthALook.length})`,
            count: worthALook.length,
        },
        {
            id: OVERVIEW_SECTION.flagged,
            label: `Flagged (${flagged.length})`,
            count: flagged.length,
        },
    ];
    // Until the user picks a section, default to the first bucket with candidates.
    const defaultSection = sections.find((section) => section.count > 0) || sections[0];
    const effectiveSectionId = activeSectionId || defaultSection.id;

    const stats = [
        { value: overview.screens_reviewed, label: "Screens Reviewed" },
        { value: overview.high_confidence_count, label: "High Confidence" },
        { value: overview.to_review_count, label: "Top Applicants" },
        { value: worthALook.length, label: "Worth a Look" },
        { value: flagged.length, label: "Flagged" },
    ];

    return (
        <div className="flex w-full flex-col gap-10 px-4 py-6 sm:px-6 lg:py-8">
            <div className="flex flex-col items-start gap-5">
                <BodyText className="!mb-0">
                    {overview.intro}
                    {refreshedAt && (
                        <span style={{ color: "var(--text-deemphasis)" }}>
                            {` | as of ${formatAsOfTimestamp(refreshedAt)}`}
                        </span>
                    )}
                </BodyText>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap">
                    {stats.map((stat) => (
                        <div
                            key={stat.label}
                            className="min-w-0 rounded-lg bg-white px-4 py-3 shadow sm:min-w-[8.5rem] sm:flex-1"
                        >
                            <Heading4 className="!mb-0">{stat.value}</Heading4>
                            <XsmallText>{stat.label}</XsmallText>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex min-w-0 flex-col gap-6">
                <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                    <JobSettingsSectionNav
                        orientation="horizontal"
                        sections={sections}
                        activeSectionId={effectiveSectionId}
                        onSectionSelect={setActiveSectionId}
                        className="lg:hidden"
                    />
                    <JobSettingsSectionNav
                        sections={sections}
                        activeSectionId={effectiveSectionId}
                        onSectionSelect={setActiveSectionId}
                        className="hidden lg:sticky lg:top-0 lg:block lg:w-44 lg:shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                        {effectiveSectionId === OVERVIEW_SECTION.highConfidence && (
                            <section className="flex min-w-0 flex-col gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <Heading6 className="!mb-0">
                                        High confidence for recruiter screen
                                    </Heading6>
                                    {overview.top_rated_subtitle && (
                                        <XsmallText>{overview.top_rated_subtitle}</XsmallText>
                                    )}
                                </div>
                                <CandidateList
                                    candidates={topRated}
                                    emptyCopy={overview.top_rated_empty_copy}
                                    onOpenApplicant={onOpenApplicant}
                                    onSendCalendarLink={sendCalendarLink}
                                    sendingCandidateId={sendingCandidateId}
                                    getMenuItems={getMenuItems}
                                    asCards
                                />
                            </section>
                        )}

                        {effectiveSectionId === OVERVIEW_SECTION.toReview && (
                            <section className="flex min-w-0 flex-col gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <Heading6 className="!mb-0">Top applicants to review</Heading6>
                                    <XsmallText>
                                        The best of who applied · match rating beside each name
                                    </XsmallText>
                                </div>
                                <CandidateList
                                    candidates={toReview}
                                    emptyCopy={overview.to_review_empty_copy}
                                    onOpenApplicant={onOpenApplicant}
                                    onSendCalendarLink={sendCalendarLink}
                                    sendingCandidateId={sendingCandidateId}
                                    getMenuItems={getMenuItems}
                                    asCards
                                />
                                {overview.to_review_extra > 0 && (
                                    <XsmallText className="-mt-1">
                                        +{overview.to_review_extra} more in June
                                    </XsmallText>
                                )}
                            </section>
                        )}

                        {effectiveSectionId === OVERVIEW_SECTION.worthALook && (
                            <section
                                className="flex min-w-0 flex-col gap-3"
                                data-cy="overview-worth-a-look"
                            >
                                <div className="flex flex-col gap-1.5">
                                    <Heading6 className="!mb-0">Worth a look, not ranked</Heading6>
                                </div>
                                <CandidateList
                                    candidates={worthALook}
                                    emptyCopy={
                                        overview.worth_a_look_empty_copy ||
                                        "No one rated 3 in this period."
                                    }
                                    onOpenApplicant={onOpenApplicant}
                                    onSendCalendarLink={sendCalendarLink}
                                    sendingCandidateId={sendingCandidateId}
                                    getMenuItems={getMenuItems}
                                    asCards
                                />
                            </section>
                        )}

                        {effectiveSectionId === OVERVIEW_SECTION.flagged && (
                            <section
                                className="flex min-w-0 flex-col gap-3"
                                data-cy="overview-flagged"
                            >
                                <div className="flex flex-col gap-1.5">
                                    <Heading6 className="!mb-0">Flagged for your review</Heading6>
                                    <XsmallText>
                                        Matched bypass rules or needs a human look
                                    </XsmallText>
                                </div>
                                <CandidateList
                                    candidates={flagged}
                                    emptyCopy={
                                        overview.flagged_empty_copy ||
                                        "No one was flagged for review in this period."
                                    }
                                    onOpenApplicant={onOpenApplicant}
                                    onSendCalendarLink={sendCalendarLink}
                                    sendingCandidateId={sendingCandidateId}
                                    getMenuItems={getMenuItems}
                                    showFlagReason
                                    asCards
                                />
                            </section>
                        )}
                    </div>
                </div>
            </div>

            <RejectApplicantModal
                isOpen={Boolean(rejectTarget)}
                onClose={() => {
                    if (!isRejecting) setRejectTarget(null);
                }}
                onConfirm={handleRejectConfirm}
                count={1}
                applicantName={rejectTarget?.name}
                companyId={companyId}
                token={token}
                isLoading={isRejecting}
            />
        </div>
    );
}
